import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { envApiUrl } from './envApiUrl';

export const useAskAI = (prompt: string) => {
  return useQuery({
    queryKey: ['useAskAI'],
    gcTime: 1000 * 60 * 60,
    queryFn: async () => await fetchAskUI(prompt),
  });
};

export const fetchAskUI = async (prompt: string) => {
  try {
    // First, get the CSRF token
    const csrfResponse = await axios.get(envApiUrl + '/auth/csrf-token', {
      withCredentials: true,
    });
    const csrfToken = csrfResponse.data.csrfToken;

    // Then make the askAI request with the CSRF token
    return await axios
      .post(
        envApiUrl + '/mydays/askAI',
        { prompt },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrfToken,
          },
          withCredentials: true,
        },
      )
      .then((response) => response.data as string)
      .catch((error) => {
        console.error('Error message: ', error.response?.message);
        if (error.response?.status === 403) {
          console.error('Error message: ', error.response.message);
          // Remove the redirect to prevent infinite loops
        }
        return '';
      });
  } catch (error) {
    console.error('CSRF token fetch error: ', error);
    return '';
  }
};

export const useAskAIAnalysis = (mydayId: string) => {
  return useQuery({
    queryKey: ['useAskAIAnalysis', mydayId],
    queryFn: async () => await fetchAskAIAnalysis(mydayId),
  });
};

export type AskAIAnalysisResponse = {
  data: string;
  message: string;
  status: string;
};

export type AskAIAnalysisResponseError = {
  message: string;
  status: string;
};

export type AskAIQuestionResponse = {
  data: string;
  message: string;
  status: string;
};

export type AskAIQuestionResponseError = {
  message: string;
  status: string;
};

export const fetchAskAIAnalysis = async (mydayId: string) => {
  try {
    // First, get the CSRF token
    const csrfResponse = await axios.get(envApiUrl + '/auth/csrf-token', {
      withCredentials: true,
    });
    const csrfToken = csrfResponse.data.csrfToken;

    // Then make the askAIAnalysis request with the CSRF token
    return await axios
      .post(
        envApiUrl + '/mydays/askAIAnalysis',
        { id: mydayId },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrfToken,
          },
          withCredentials: true,
        },
      )
      .then((response) => response.data as AskAIAnalysisResponse)
      .catch((error) => {
        console.error('Error message: ', error.response?.message);
        if (error.response?.status === 403) {
          console.error('Error message: ', error.response.message);
          // Remove the redirect to prevent infinite loops
        }
        return {
          data: '',
          message: error.response?.message || 'Unknown error',
          status: 'error',
        } as AskAIAnalysisResponseError;
      });
  } catch (error) {
    console.error('CSRF token fetch error: ', error);
    return { data: '', message: 'CSRF token fetch failed', status: 'error' } as AskAIAnalysisResponseError;
  }
};

export const fetchAskAIDayAnalysis = async (mydayId: string) => {
  try {
    // First, get the CSRF token
    const csrfResponse = await axios.get(envApiUrl + '/auth/csrf-token', {
      withCredentials: true,
    });
    const csrfToken = csrfResponse.data.csrfToken;

    // Then make the askAIDayAnalysis request with the CSRF token
    return await axios
      .post(
        envApiUrl + '/mydays/askAIDayAnalysis',
        { id: mydayId },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrfToken,
          },
          withCredentials: true,
        },
      )
      .then((response) => response.data as AskAIAnalysisResponse)
      .catch((error) => {
        console.error('Error message: ', error.response?.message);
        if (error.response?.status === 403) {
          console.error('Error message: ', error.response.message);
        }
        return {
          data: '',
          message: error.response?.message || 'Unknown error',
          status: 'error',
        } as AskAIAnalysisResponseError;
      });
  } catch (error) {
    console.error('CSRF token fetch error: ', error);
    return { data: '', message: 'CSRF token fetch failed', status: 'error' } as AskAIAnalysisResponseError;
  }
};

export const fetchAskAIHistoricalAnalysis = async (mydayId: string) => {
  try {
    // First, get the CSRF token
    const csrfResponse = await axios.get(envApiUrl + '/auth/csrf-token', {
      withCredentials: true,
    });
    const csrfToken = csrfResponse.data.csrfToken;

    // Then make the askAIHistoricalAnalysis request with the CSRF token
    return await axios
      .post(
        envApiUrl + '/mydays/askAIHistoricalAnalysis',
        { id: mydayId },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrfToken,
          },
          withCredentials: true,
        },
      )
      .then((response) => response.data as AskAIAnalysisResponse)
      .catch((error) => {
        console.error('Error message: ', error.response?.message);
        if (error.response?.status === 403) {
          console.error('Error message: ', error.response.message);
        }
        return {
          data: '',
          message: error.response?.message || 'Unknown error',
          status: 'error',
        } as AskAIAnalysisResponseError;
      });
  } catch (error) {
    console.error('CSRF token fetch error: ', error);
    return { data: '', message: 'CSRF token fetch failed', status: 'error' } as AskAIAnalysisResponseError;
  }
};

export const fetchAskAIQuestion = async (question: string, mydayId?: number) => {
  try {
    // First, get the CSRF token
    const csrfResponse = await axios.get(envApiUrl + '/auth/csrf-token', {
      withCredentials: true,
    });
    const csrfToken = csrfResponse.data.csrfToken;

    // Then make the askAIQuestion request with the CSRF token
    return await axios
      .post(
        envApiUrl + '/mydays/askAIQuestion',
        { question, mydayId },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrfToken,
          },
          withCredentials: true,
        },
      )
      .then((response) => response.data as AskAIQuestionResponse)
      .catch((error) => {
        console.error('Error message: ', error.response?.message);
        if (error.response?.status === 403) {
          console.error('Error message: ', error.response.message);
        }
        return {
          data: '',
          message: error.response?.message || 'Unknown error',
          status: 'error',
        } as AskAIQuestionResponseError;
      });
  } catch (error) {
    console.error('CSRF token fetch error: ', error);
    return { data: '', message: 'CSRF token fetch failed', status: 'error' } as AskAIQuestionResponseError;
  }
};
